import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Loading, LoaderSize } from 'framework/ui/mui-builder';
import { omit } from 'lodash';
import { useClientPortal } from '../clientPortal/useClientPortal';
import {
  ClientPortalRedmineRequest,
  ClientPortalResponse,
  ClientPortalUser,
  ProjectClientPortalData,
} from '../clientPortal/types';
import useClientPortalService from '../clientPortal/useClientPortalService';
import { useProjectDataContext } from 'domain/shared/newProject';
import { clientPortalUsersMock, getClientPortalVisibleUsers } from '../clientPortal/mockData';

interface ClientPortalProjectContextData {
  data: ClientPortalResponse;
  refetch: () => Promise<unknown>;
  clientPortalData: ProjectClientPortalData;
  users: ClientPortalUser[];
  activeRequests: ClientPortalRedmineRequest[];
  isActiveRequestsLoading: boolean;
  isClientPortalDataReloading: boolean;
  budgetEndDate?: string;
  refetchClientPortalData: () => Promise<unknown>;
  refetchActiveRequests: () => Promise<ClientPortalRedmineRequest[]>;
  refetchClientPortalContext: () => Promise<void>;
}

const ClientPortalProjectDataContext = createContext<Nullable<ClientPortalProjectContextData>>(null);

export const useClientPortalProjectDataContext = () => {
  const context = useContext(ClientPortalProjectDataContext);
  if (!context) {
    throw new Error('useClientPortalProjectDataContext must be used within ClientPortalProjectDataContext');
  }
  return context;
};

export const ClientPortalProjectContext = ({
  id,
  children,
}: PropsWithChildren<{
  id: string;
}>) => {
  const { data: projectContextData } = useProjectDataContext();
  const { getActiveUserAccessRequests } = useClientPortalService();
  const getActiveUserAccessRequestsRef = useRef(getActiveUserAccessRequests);
  const [activeRequests, setActiveRequests] = useState<ClientPortalRedmineRequest[]>([]);
  const [isActiveRequestsLoading, setIsActiveRequestsLoading] = useState(true);
  const { data, refetch, isDataReloading } = useClientPortal(id);

  useEffect(() => {
    getActiveUserAccessRequestsRef.current = getActiveUserAccessRequests;
  }, [getActiveUserAccessRequests]);

  const refetchActiveRequests = useCallback(async () => {
    setIsActiveRequestsLoading(true);
    try {
      const requests = await getActiveUserAccessRequestsRef.current(id);
      setActiveRequests(requests);
      return requests;
    } finally {
      setIsActiveRequestsLoading(false);
    }
  }, [id]);

  const refetchClientPortalContext = useCallback(async () => {
    await Promise.all([refetch(), refetchActiveRequests()]);
  }, [refetch, refetchActiveRequests]);

  useEffect(() => {
    refetchActiveRequests();
  }, [refetchActiveRequests]);

  const clientPortalData = useMemo<ProjectClientPortalData>(() => {
    const visibleUsers = getClientPortalVisibleUsers(data?.clientPortal?.users || clientPortalUsersMock);
    const requestedUsers = data?.clientPortal?.requestedUsers || [];

    return {
      ...projectContextData,
      clients: visibleUsers,
      clientPortalUsers: [...visibleUsers, ...requestedUsers],
      clientPortalRedmineRequests: activeRequests,
      tasks: data?.clientPortal?.tasks || [],
    };
  }, [
    projectContextData,
    activeRequests,
    data?.clientPortal?.requestedUsers,
    data?.clientPortal?.tasks,
    data?.clientPortal?.users,
  ]);

  const { contextValue } = useMemo(() => {
    if (!data) {
      return {
        contextValue: null,
      };
    }

    return {
      contextValue: {
        data: omit(data, '__typename'),
        refetch: refetchClientPortalContext,
        clientPortalData,
        users: data?.clientPortal?.users ?? [],
        activeRequests,
        isActiveRequestsLoading,
        isClientPortalDataReloading: isDataReloading,
        budgetEndDate: projectContextData?.budget?.endDate,
        refetchClientPortalData: refetch,
        refetchActiveRequests,
        refetchClientPortalContext,
      },
    };
  }, [
    activeRequests,
    data,
    isActiveRequestsLoading,
    isDataReloading,
    projectContextData?.budget?.endDate,
    clientPortalData,
    refetch,
    refetchActiveRequests,
    refetchClientPortalContext,
  ]);

  if (!data) {
    return <Loading isInitialPosition size={LoaderSize.Small} />;
  }

  return (
    <ClientPortalProjectDataContext.Provider value={contextValue}>{children}</ClientPortalProjectDataContext.Provider>
  );
};
